import React, { useEffect, useState, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { useAuthStore, hasMinimumRole, ROLES } from '../lib/auth';
import { auditAPI, venueAPI } from '../lib/api';
import { 
  FileSearch, 
  Plus, 
  Download,
  Star,
  Loader2,
  AlertCircle,
  Building2,
  Upload,
  Camera
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errorMessage';

const CameraUpload = ({ label }) => (
  <div className="flex items-center gap-2 mt-3 p-3 border border-dashed border-[#3F3F46] rounded-md bg-[#121212]">
    <Camera className="w-4 h-4 text-[#A1A1AA]" />
    <span className="text-sm text-[#A1A1AA] flex-1 truncate">Photo evidence: {label}</span>
    <Button type="button" variant="outline" className="h-8 text-xs bg-[#27272A] border-[#3F3F46] text-white hover:bg-[#3F3F46]">
      Upload
    </Button>
  </div>
);

const AuditCard = ({ audit }) => {
  const [downloading, setDownloading] = useState(false);

  const arrayBufferToBase64 = (buffer) => {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;

    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.subarray(i, i + chunkSize);
      binary += String.fromCharCode(...chunk);
    }
    return btoa(binary);
  };

  const getScoreColor = (percentage) => {
    if (percentage >= 80) return 'text-[#00E676]';
    if (percentage >= 60) return 'text-[#FFB300]';
    return 'text-[#FF3B30]';
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const isAndroidNative = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';

      if (isAndroidNative) {
        const response = await auditAPI.getPDFBinary(audit.id);
        const { Filesystem, Directory } = await import('@capacitor/filesystem');
        const { FilePicker } = await import('@capawesome/capacitor-file-picker');
        const filename = `audit_${audit.id}.pdf`;
        const base64Data = arrayBufferToBase64(response.data);

        const tempPath = `audit_exports/${filename}`;
        await Filesystem.writeFile({
          path: tempPath,
          data: base64Data,
          directory: Directory.Documents,
          recursive: true,
        });
        const tempUri = await Filesystem.getUri({ path: tempPath, directory: Directory.Documents });

        try {
          const pickedDirectory = await FilePicker.pickDirectory();
          await FilePicker.copyFile({
            from: tempUri.uri,
            to: `${pickedDirectory.path}/${filename}`,
            overwrite: true,
          });
          toast.success('PDF saved to selected folder');
        } catch (pickError) {
          await Filesystem.writeFile({
            path: `Download/${filename}`,
            data: base64Data,
            directory: Directory.ExternalStorage,
            recursive: true,
          });
          toast.success('Folder selection cancelled. PDF saved to Downloads');
        }

        try {
          await Filesystem.deleteFile({ path: tempPath, directory: Directory.Documents });
        } catch (cleanupError) {}

        return;
      }

      const response = await auditAPI.getPDF(audit.id);
      const { pdf, filename } = response.data;
      
      const byteCharacters = atob(pdf);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to download PDF'));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="card-rtb" data-testid={`audit-${audit.id}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-md bg-[#27272A]">
            <FileSearch className="w-6 h-6 text-[#FF6600]" />
          </div>
          <div>
            <h3 className="text-white font-semibold">{audit.title}</h3>
            <p className="text-[#52525B] text-sm">{audit.venue_name}</p>
          </div>
        </div>
        {audit.is_draft && (
          <span className="px-2 py-1 text-xs font-bold uppercase bg-[#FFB300]/20 text-[#FFB300] rounded">
            Draft
          </span>
        )}
      </div>

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Star className={`w-5 h-5 ${getScoreColor(audit.percentage)}`} />
          <span className={`text-2xl font-bold font-heading ${getScoreColor(audit.percentage)}`}>
            {audit.percentage.toFixed(1)}%
          </span>
        </div>
        <span className="text-[#52525B] text-sm">
          {audit.total_score}/{audit.max_score} points
        </span>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-[#3F3F46]">
        <div className="text-[#52525B] text-sm">
          <span>By {audit.created_by_name}</span>
          <span className="mx-2">•</span>
          <span>{new Date(audit.created_at).toLocaleDateString()}</span>
        </div>
        {!audit.is_draft && (
          <Button
            onClick={handleDownloadPDF}
            disabled={downloading}
            className="btn-rtb-secondary"
          >
            {downloading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                PDF
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

const CreateAuditDialog = ({ venues, onAuditCreated }) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    venue_id: '',
    title: '',
    items: [],
    is_draft: false,
  });

  const categories = [
    'Cleanliness',
    'Food Safety',
    'Staff Standards',
    'Customer Service',
    'Documentation',
    'Health & Safety',
  ];

  const handleAddItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, {
        label: '',
        category: categories[0],
        weight: 1,
        score: null,
        notes: '',
      }]
    }));
  };

  const updateItem = (index, updates) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], ...updates };
      return { ...prev, items: newItems };
    });
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleCSVUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // FIX: Just enforce that it ends in .csv, as browsers handle MimeTypes differently
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error("Strict Policy: Please upload a valid .csv file format.");
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const rows = text.split(/\r?\n/).filter(row => row.trim().length > 0);
        
        const startIndex = rows[0].toLowerCase().includes('label') ? 1 : 0;
        
        const parsedItems = rows.slice(startIndex).map(row => {
          const cols = row.split(',').map(item => item?.trim() || '');
          
          return {
            label: cols[0] || 'Unnamed Item',
            category: categories[0], 
            weight: cols.length > 9 && !isNaN(cols[9]) ? parseInt(cols[9], 10) : 1,
            score: null,
            notes: '',
          };
        });

        if (parsedItems.length === 0) {
            toast.error("No valid items found in CSV.");
            return;
        }

        setFormData(prev => ({
          ...prev,
          items: [...prev.items, ...parsedItems]
        }));
        
        toast.success(`Imported ${parsedItems.length} EHO items from CSV`);
      } catch (err) {
        console.error("CSV Parse Error:", err);
        toast.error("Failed to parse CSV. Ensure formatting matches EHO template.");
      } finally {
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    };
    reader.readAsText(file);
  };

  const handleSubmit = async (isDraft = false) => {
    if (!formData.venue_id || !formData.title) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.items.length === 0) {
      toast.error('Please add at least one audit item');
      return;
    }

    setLoading(true);
    try {
      await auditAPI.create({
        ...formData,
        is_draft: isDraft,
      });
      toast.success(isDraft ? 'Draft saved' : 'Audit completed');
      setOpen(false);
      setStep(1);
      setFormData({ venue_id: '', title: '', items: [], is_draft: false });
      onAuditCreated();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create audit'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="btn-rtb-primary" data-testid="create-audit-btn">
          <Plus className="w-5 h-5 mr-2" />
          New Audit
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#1E1E1E] border-[#3F3F46] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold uppercase">
            {step === 1 ? 'EHO Audit Builder' : 'Execute Audit Scoring'}
          </DialogTitle>
        </DialogHeader>

        {step === 1 ? (
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-sm font-bold uppercase text-[#A1A1AA]">Venue *</Label>
              <Select value={formData.venue_id} onValueChange={(v) => setFormData({ ...formData, venue_id: v })}>
                <SelectTrigger className="input-rtb mt-2">
                  <SelectValue placeholder="Select venue" />
                </SelectTrigger>
                <SelectContent className="bg-[#1E1E1E] border-[#3F3F46]">
                  {venues.map(venue => (
                    <SelectItem key={venue.id} value={venue.id} className="text-white">
                      {venue.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-bold uppercase text-[#A1A1AA]">Audit Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Q4 2024 Full Compliance Audit"
                className="input-rtb mt-2"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-sm font-bold uppercase text-[#A1A1AA]">Audit Checkpoints</Label>
                <div className="flex gap-2">
                  <div className="relative">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleCSVUpload}
                      ref={fileInputRef}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      title="Upload CSV Template"
                    />
                    <Button type="button" className="btn-rtb-secondary h-8 text-sm">
                      <Upload className="w-4 h-4 mr-1" />
                      Import EHO CSV
                    </Button>
                  </div>
                  <Button
                    type="button"
                    onClick={handleAddItem}
                    className="btn-rtb-secondary h-8 text-sm"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Manual
                  </Button>
                </div>
              </div>
              <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                {formData.items.map((item, index) => (
                  <div key={index} className="p-3 bg-[#27272A] rounded-md space-y-3 border border-[#3F3F46]">
                    <div className="flex flex-col sm:flex-row gap-3">
                      {/* FIX: Using Textarea instead of Input for long checkpoint descriptions */}
                      <Textarea
                        value={item.label}
                        onChange={(e) => updateItem(index, { label: e.target.value })}
                        placeholder="Checkpoint description..."
                        className="flex-1 min-h-[80px] bg-[#121212] border border-[#3F3F46] text-white p-2 rounded-md focus:ring-2 focus:ring-[#FF6600]"
                      />
                      <div className="flex sm:flex-col gap-2 shrink-0">
                        <Select value={item.category} onValueChange={(v) => updateItem(index, { category: v })}>
                          <SelectTrigger className="w-full sm:w-40 bg-[#1E1E1E] border-[#3F3F46] h-10 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1E1E1E] border-[#3F3F46]">
                            {categories.map(cat => (
                              <SelectItem key={cat} value={cat} className="text-white">{cat}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="h-10 w-full sm:w-40 bg-[#FF3B30]/20 hover:bg-[#FF3B30]/30 text-[#FF3B30]"
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center pl-1">
                       <span className="text-xs text-[#FFB300] bg-[#FFB300]/10 px-2 py-0.5 rounded">
                         Weight: {item.weight}
                       </span>
                    </div>
                  </div>
                ))}
                {formData.items.length === 0 && (
                  <p className="text-[#52525B] text-center py-8 border border-dashed border-[#3F3F46] rounded-md">
                    No checkpoints. Import an EHO CSV or add manually.
                  </p>
                )}
              </div>
            </div>

            <Button
              onClick={() => setStep(2)}
              disabled={!formData.venue_id || !formData.title || formData.items.length === 0}
              className="btn-rtb-primary w-full"
            >
              Next: Score Items
            </Button>
          </div>
        ) : (
          <div className="space-y-4 mt-4">
            <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
              {formData.items.map((item, index) => (
                <div key={index} className="p-4 bg-[#27272A] rounded-md border border-[#3F3F46]">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 gap-3">
                    <div>
                      <p className="text-white font-medium">{item.label}</p>
                      <p className="text-[#52525B] text-xs mt-1">{item.category}</p>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      {[1, 2, 3, 4, 5].map(score => (
                        <button
                          key={score}
                          onClick={() => updateItem(index, { score })}
                          className={`w-10 h-10 rounded-md font-bold transition-colors ${
                            item.score === score 
                              ? score >= 4 ? 'bg-[#00E676] text-black' : score >= 3 ? 'bg-[#FFB300] text-black' : 'bg-[#FF3B30] text-white'
                              : 'bg-[#121212] border border-[#3F3F46] text-[#A1A1AA] hover:bg-[#3F3F46]'
                          }`}
                        >
                          {score}
                        </button>
                      ))}
                    </div>
                  </div>
                  <Textarea
                    value={item.notes}
                    onChange={(e) => updateItem(index, { notes: e.target.value })}
                    placeholder="Notes (optional)"
                    className="input-rtb min-h-[60px]"
                  />
                  <CameraUpload label={item.label || 'Unnamed checkpoint'} />
                </div>
              ))}
            </div>

            <div className="flex gap-3 pt-4 border-t border-[#3F3F46]">
              <Button onClick={() => setStep(1)} className="btn-rtb-secondary flex-1">Back</Button>
              <Button onClick={() => handleSubmit(true)} disabled={loading} className="btn-rtb-secondary flex-1">Save Draft</Button>
              <Button onClick={() => handleSubmit(false)} disabled={loading} className="btn-rtb-primary flex-1">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Complete Audit'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const AuditsPage = () => {
  const { user } = useAuthStore();
  const [audits, setAudits] = useState([]);
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [auditsRes, venuesRes] = await Promise.all([
        auditAPI.list(),
        venueAPI.list(),
      ]);
      setAudits(auditsRes.data);
      setVenues(venuesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load audits');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#FF6600] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tight">
            Audits
          </h1>
          <p className="text-[#A1A1AA] mt-1">
            Formal compliance audits and reports
          </p>
        </div>
        
        {hasMinimumRole(user, ROLES.OPS) && (
          <CreateAuditDialog venues={venues} onAuditCreated={fetchData} />
        )}
      </div>

      {audits.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2">
          {audits.map(audit => (
            <AuditCard key={audit.id} audit={audit} />
          ))}
        </div>
      ) : (
        <div className="card-rtb text-center py-12">
          <AlertCircle className="w-12 h-12 text-[#3F3F46] mx-auto mb-4" />
          <h3 className="font-heading text-xl font-bold text-white uppercase mb-2">No Audits Yet</h3>
          <p className="text-[#A1A1AA] mb-6">Start your first formal compliance audit.</p>
          {hasMinimumRole(user, ROLES.OPS) && (
            <CreateAuditDialog venues={venues} onAuditCreated={fetchData} />
          )}
        </div>
      )}
    </div>
  );
};

export default AuditsPage;